import * as angular from 'angular';
import * as _ from 'lodash';

import template from './count.tpl.html';
import {
	Counts, LineCounts, LineEquipmentGroup, LineEquipmentItem, LineItem,
	LineJunctionItem
} from "../../models/counts/lineCounts";
import { Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { apsEnums } from "../../shared/dto/aps.enums";
import { States, Intervals } from "../../shared/config/constant.module";
import { apsDisplayEnums } from "../../shared/dto/enums";
import CountsServiceName, { CountHub, CountsService } from "../../services/counts/counts.service";
import UIUtilityServiceName, { EnumOption, UIUtilityService } from "../../services/utility/uiUtility.service";
import LineConfigsServiceName, { LineConfigsService } from "../../services/lineConfigs/lineConfigs.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import NavigationServiceName, { NavigationService } from "../../services/navigation/navigation.service";
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import GroupRejectsServiceName, { GroupRejectsService } from "../../services/counts/groupRejects.service";
import EquipmentRejectsName, { openEquipmentRejects } from "./equipmentRejects.controller";
import { IBtn, IBtnArrayOptions } from '../dashboard/btnarray.interfaces';
import { StateParams } from '../../common';
import GobWeightsGraph from '../dashboard/gobWeightsGraph.directive'; 

let requires = [CountsServiceName, UIUtilityServiceName, LineConfigsServiceName, LoaderServiceName,
	NavigationServiceName, AuthDataServiceName, GroupRejectsServiceName, EquipmentRejectsName, GobWeightsGraph ];

export interface ICountControllerBase {
	countsService: CountsService;
	usePercentGobsChanged(): void;
	plant: apsViewModels.PlantNavigation;
	model: LineCounts;
}

export interface ICountController extends ICountControllerBase {
	$stateParams: StateParams,
	areas: CountArea[];
	plant: Plant;
	shop: apsViewModels.ShopNavigation;
	hub: CountHub;
	countDisplayIntervalOptions: EnumOption[];
	countsInterval: apsDisplayEnums.CountDisplayInterval;
	canDisableLine: boolean;
	viewRejectDetail(line: LineItem, equipment: LineEquipmentItem): void;
	viewGroupRejectDetail(equipmentGroup: LineEquipmentGroup): void;
	countsIntervalChanged(): void;
	calculateSpeed(gobsCount: Counts): number;
	toggleLineActive(line: LineItem): void;
	wareLossClicked(): void;
	showHelp(helpScreen: string): void;
	toggleHelpWindow(): void;
	showHelpWindow: boolean;
	helpText: string;
	getLossPercentClass(equip: LineEquipmentItem): string;
	getEquipmentClass(equip: LineEquipmentItem): string;
	getRowClass(equip: LineEquipmentItem): string;
	getSynchronizedBlinkClass(): string;
	cavities: apsViewModels.CavityCountSummary[];
	hasCavities: boolean;
	userId: string;
	navigationUrls: IBtnArrayOptions;
	shopId: any
	dumpGateDuration: number;
	updateDumpGateDuration(): void;
	blinkClass: string;
}

export interface CountRow {
	isLine: boolean;
	line: LineItem;
	junction: LineJunctionItem;
	cssClass: string;
}

export interface CountArea {
	rows: CountRow[];
}

export interface RejectTolerances {
  rejectMinPercentage: number;
  rejectMaxPercentage: number;
}

export default angular.module('aps.controller.counts', requires)
	.config(countsConfig)
	.controller('CountsCtrl', countsCtrl)
	.name;

function countsConfig($stateProvider: angular.ui.IStateProvider) {
	$stateProvider.state({
		name: States.COUNTS_HORIZONTAL,
		url: '/counts',
		controller: 'CountsCtrl as vm',
		template: template,
		data: { pageTitle: 'Counts Data', v2Uri: 'aps/dashboard/shop/counts-dashboard' }
	});
}

export function setUsePercentGobsChangedMethod(controller: ICountControllerBase) {
	controller.usePercentGobsChanged = () => {
		controller.model.usePercentGobs = !controller.model.usePercentGobs;
		usePercentGobsProducedChanged(controller.countsService, controller.plant.id, controller.model.usePercentGobs);
	}
}

export function usePercentGobsProducedChanged(countsService: CountsService, plantId: string, usePercentGobs: boolean) {
	countsService.setUsePercentGobs(plantId, usePercentGobs);
}

export function lineCountsLoaded(controller: ICountControllerBase) {
	let usePercentGobs = controller.countsService.getUsePercentGobs(controller.plant);
	controller.model.usePercentGobs = usePercentGobs;
}

function countsCtrl(
	$stateParams: StateParams,
	countsService: CountsService,
	lineConfigsService: LineConfigsService,
	loaderService: LoaderService,
	navigationService: NavigationService,
	authDataService: AuthDataService,
	groupRejectsService: GroupRejectsService,
	uiUtilityService: UIUtilityService,
	$scope: angular.IScope,
	$timeout: angular.ITimeoutService,
	$modal: mm.foundation.modal,
	$state: angular.ui.IStateService,
	$interval: angular.IIntervalService): ICountController {
	let equipmentRejectTolerances = {};
	let intervalMinutes: number;

	let vm: ICountController = {
		$stateParams: $stateParams,
		countsService: countsService,
		model: null,
		areas: [],
		plant: null,
		shop: null,
		hub: null,
		shopId: null,
		canDisableLine: false,
		countDisplayIntervalOptions: uiUtilityService.getListOptionsFromEnum(
			apsDisplayEnums.CountDisplayInterval,
			null, null, o => o),
		countsInterval: authDataService.getCountInterval(),
		viewRejectDetail: null,
		viewGroupRejectDetail: null,
		calculateSpeed: null,
		countsIntervalChanged: null,
		toggleLineActive: null,
		wareLossClicked: null,
		showHelp: null,
		showHelpWindow: false,
		helpText: null,
		toggleHelpWindow: null,
		getLossPercentClass: null,
		getEquipmentClass: null,
		getRowClass: null,
		getSynchronizedBlinkClass: null,
		cavities: [],
		hasCavities: false,
		userId: null,
		navigationUrls: {
			buttons: [
				{ 'name': 'Horizontal Layout', 'value': 'horizontal', 'selected': true },
				{ 'name': 'Vertical Layout', 'value': 'vertical', 'selected': false },
				{ 'name': 'Vertical Layout (v2)', 'value': 'vc2', 'selected': false },
			],
			select: (button: IBtn) => {
				let stateParams = {
					plantId: vm.$stateParams.plantId,
					shopId: vm.$stateParams.shopId,
					userId: vm.$stateParams.userId
				};

				switch (button.value) {
					case "horizontal": $state.go(States.COUNTS_HORIZONTAL, stateParams); break;
					case "vertical": $state.go(States.COUNTS_VERTICAL, stateParams); break;
					case "vc2": $state.go(States.COUNTS_VERTICAL_V2, stateParams); break;
				}
			}
		},
		dumpGateDuration: 0,
		updateDumpGateDuration: null,
		blinkClass: 'blink-dark-horz',
		usePercentGobsChanged: null
	};
	intervalMinutes = Intervals.IntervalSeconds[vm.countsInterval];
	vm.updateDumpGateDuration;

	vm.userId = $stateParams.userId;
	let shopId = $stateParams.shopId;
	vm.shopId = shopId;
	vm.canDisableLine = authDataService.isPlantOrAPSIOAdminForCurrentPlant();

	let plantShopsPromise = navigationService.getNavigation().then(navModel => {
		vm.plant = navModel.selectedPlant;
		vm.shop = navModel.selectedShop;
	});

	setUsePercentGobsChangedMethod(vm);

	vm.updateDumpGateDuration = () => {
		countsService.getDumpGateTimeCount(vm.plant.id, shopId, vm.countsInterval)
			.then(data => {
				return vm.dumpGateDuration = (data > intervalMinutes) ? intervalMinutes : data;
			});		
	}

	var dumpGateDurationTimer: angular.IPromise<any>;
	let createDumpGateDurationTimer = () => {
		vm.updateDumpGateDuration();
		if (dumpGateDurationTimer) {
			$interval.cancel(dumpGateDurationTimer);
		}
		dumpGateDurationTimer = $interval(vm.updateDumpGateDuration, 300000);
	}
	
	let updateBlinkClass = () => {
		if (vm.blinkClass.indexOf("light") > 0) {
			vm.blinkClass = " blink-dark-horz";
		} else {
			vm.blinkClass = " blink-light-horz";
		}
	}

	var blinkTimer: angular.IPromise<any>;
	let createBlinkTimer = () => {
		updateBlinkClass();
		if (blinkTimer) {
			$interval.cancel(blinkTimer);
		}
		blinkTimer = $interval(updateBlinkClass, 600);
	}

	$scope.$on("$destroy", function() {
        if (dumpGateDurationTimer) {
            $interval.cancel(dumpGateDurationTimer);
						$interval.cancel(blinkTimer);
        }
    });

	let countsPromise = plantShopsPromise.then(() => {	
		createDumpGateDurationTimer();
		createBlinkTimer();
		return countsService.getLineCounts(shopId, vm.plant, vm.countsInterval, vm.canDisableLine, true)
			.then(result => {
				vm.model = result;
				lineCountsLoaded(vm);
				vm.areas = mapModelToAreas(vm.model);

				let rejects = vm.model.shopConfig.jobRuns[0].machineRejectPercentages;
				if (!!rejects) { 
					vm.areas.forEach(area => {
						area.rows.forEach(row => {
							if (!row.isLine) { return; }
							row.line.equipment.forEach(equipment => {
								let equipWithRejectPercentage = rejects.filter(item => item.selectedMachine === equipment.name );
								if (equipWithRejectPercentage.length > 0) {
									let tolerances: RejectTolerances = {
										rejectMinPercentage: equipWithRejectPercentage[0].rejectedMinPercentage,
										rejectMaxPercentage: equipWithRejectPercentage[0].rejectedMaxPercentage
									};
									equipmentRejectTolerances[equipment.name] = tolerances;
								}
							});
						});
					});
				}

				for (let id in vm.model.equipment) {
					if (vm.model.equipment[id].type === apsEnums.LineEquipmentType.HEYE
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.CheckDetector
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.MX4
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.Mcal
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.Multi
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.FlexBC
						|| vm.model.equipment[id].type === apsEnums.LineEquipmentType.FlexM
					) {
						vm.hasCavities = true;
						break;
					}
				}
			});		
	});

	vm.wareLossClicked = (): void => {
		let stateParams = {
			plantId: vm.$stateParams.plantId,
			shopId: vm.$stateParams.shopId,
			userId: vm.$stateParams.userId,
		};
		$state.go(States.PLANT_WARE_LOSS_DASHBOARD, stateParams);
	}

	vm.toggleHelpWindow = (): void => {
		vm.showHelpWindow = !vm.showHelpWindow;
	}

	vm.showHelp = (helpScreen: string): void => {
		vm.helpText = getCountsHelpText(helpScreen);
		vm.toggleHelpWindow();
	}

	vm.getLossPercentClass = (equipment: any): string => {
    let rejectTolerance = equipmentRejectTolerances[equipment.name];
    if(equipment.rejectCount && !!rejectTolerance
		    && (	 Number(equipment.rejectCount.percent) > rejectTolerance.rejectMaxPercentage
              || Number(equipment.rejectCount.percent) < rejectTolerance.rejectMinPercentage) ) {
      return 'fail-percentage'; 
    }
    return 'counts-equipment-details';
  };

	vm.getEquipmentClass = (equip: LineEquipmentItem): string => {
		if (!equip) { return ''; }
		if (equip.isDisabled) { return 'equipment-disabled'; }
		
		let rejectTolerance = equipmentRejectTolerances[equip.name];
		var equipmentClass = 'equip-status-good-box';
		if (equip.rejectCount && !!rejectTolerance
       && (Number(equip.rejectCount.percent) > rejectTolerance.rejectMaxPercentage 
				|| Number(equip.rejectCount.percent) < rejectTolerance.rejectMinPercentage)) {
			equipmentClass =  'equip-status-error-box';

			if(  vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentShift
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.RollingHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes) {
				equipmentClass += ' blinking';
			}
		}
		return equipmentClass;
	};

	vm.getLossPercentClass = (equipment: any): string => {
    let rejectTolerance = equipmentRejectTolerances[equipment.name];
    if(equipment.rejectCount && !!rejectTolerance
		    && (	 Number(equipment.rejectCount.percent) > rejectTolerance.rejectMaxPercentage
              || Number(equipment.rejectCount.percent) < rejectTolerance.rejectMinPercentage) ) {
      return 'fail-percentage'; 
    }
    return 'counts-equipment-details';
  };

	vm.getEquipmentClass = (equip: LineEquipmentItem): string => {
		if (!equip) { return ''; }
		let rejectTolerance = equipmentRejectTolerances[equip.name];
		var equipmentClass = 'equip-status-good-box';
		if (equip.rejectCount && !!rejectTolerance
      && (Number(equip.rejectCount.percent) > rejectTolerance.rejectMaxPercentage 
				|| Number(equip.rejectCount.percent) < rejectTolerance.rejectMinPercentage)) {
			equipmentClass =  'equip-status-error-box';

			if(  vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentShift
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.RollingHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes) {
					equipmentClass = vm.blinkClass;
			}
		}
		return equipmentClass;
	};

	vm.getRowClass = (equipment: any): string => {
    let rejectTolerance = equipmentRejectTolerances[equipment.name];
    var equipmentClass = '';
    if(equipment.rejectCount && !!rejectTolerance
		    && (	 Number(equipment.rejectCount.percent) > rejectTolerance.rejectMaxPercentage
              || Number(equipment.rejectCount.percent) < rejectTolerance.rejectMinPercentage) ) {
      equipmentClass = 'rejectBgVerticals';

			if(  vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.CurrentShift
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.RollingHour
				|| vm.countsInterval == apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes) {
					equipmentClass =  vm.blinkClass;
			}
			return equipmentClass;
		}
  };

	vm.toggleLineActive = (line: LineItem): void => {
		if (!vm.canDisableLine) {
			return;
		}

		let message = `Line ${line.name} is currently ${line.isDisabled ? 'disabled' : 'active'}. `
			+ `Would you like to ${line.isDisabled ? 'activate' : 'disable'} it?`;
		let subject = `${line.isDisabled ? 'Activate' : 'Disable'} Line ${line.name}?`;

		uiUtilityService.confirm(message, subject, 'Yes', 'No').then(confirmed => {
			if (!confirmed) {
				return;
			}

			let promise = lineConfigsService.toggleLine({
				id: line.id,
				shopId: line.shopId,
				isDisabled: !line.isDisabled
			}).then(() => line.isDisabled = !line.isDisabled);
			loaderService.loadingPromise(promise);
		});
	};

	vm.viewRejectDetail = (line: LineItem, equipment: LineEquipmentItem): void => {
		let modalPromise: angular.IPromise<any> = countsPromise;
		if (equipment.hasTabs || (equipment.detailDataPoints && equipment.detailDataPoints.length > 0)) {
			modalPromise = countsService.getEquipmentCounts(shopId, vm.model, equipment, vm.countsInterval)
				.then(counts => {
					vm.model.currentDetailCounts = counts;
				});
			loaderService.loadingPromise(modalPromise);
		}
		modalPromise.then(() => {
			var result = openEquipmentRejects({
				equipment: equipment,
				line: line,
				plant: vm.plant,
				shopId: shopId,
				canDisable: vm.canDisableLine,
				detail: vm.model.currentDetailCounts,
				lineConfig: vm.model
			}, $modal);

			result.then(() => {
				if (vm.model.currentDetailCounts) {
					delete vm.model.currentDetailCounts;
				}
			});
		});
	};

	vm.viewGroupRejectDetail = (equipmentGroup: LineEquipmentGroup): void => {
		vm.model.currentGroup = equipmentGroup;
		groupRejectsService.showGroupRejects({
			equipmentGroup: equipmentGroup,
			shopId: vm.shop.id,
			lineCounts: vm.model,
			countsInterval: vm.countsInterval
		});
	};
	

	vm.countsIntervalChanged = () => {
		vm.model.handleIntervalChange(vm.countsInterval);
		authDataService.setCountInterval(vm.countsInterval);		
		intervalMinutes = Intervals.IntervalSeconds[vm.countsInterval];
		vm.updateDumpGateDuration();
	};

	let promise = countsPromise.then(() => {
		vm.hub = countsService.createHub(shopId, vm.model);

		$scope.$on('$destroy', () => {
			if (vm.hub) {
				vm.hub.leaveGroup(shopId)
					.then(() => vm.hub.uninitialize());
			}
		});
	});

	loaderService.loadingPromise(promise);

	return vm;
}

function mapLineToRow(line: LineItem): CountRow {
	return {
		isLine: true,
		line: line,
		junction: null,
		cssClass: null
	};
}

function mapJunctionToRow(junction: LineJunctionItem): CountRow {
	return {
		isLine: false,
		line: null,
		junction: junction,
		cssClass: 'table' + (junction.inputs.length > junction.outputs.length
			? junction.inputs.length : junction.outputs.length).toString()
	};
}

function mapModelToAreas(model: LineCounts): CountArea[] {
	let areas = [];
	let area: CountArea = {
		rows: _.sortBy(model.lines.filter(l => l.startJunctionId === null), r => r.number)
			.map(l => mapLineToRow(l))
	};

	while (area.rows.length > 0) {
		areas.push(area);

		let previousLines = area.rows.filter(l => l.isLine).map(l => l.line);
		let rows = [];
		area.rows.forEach(row => {
			// if row is junction, add all of the output lines
			if (!row.isLine) {
				_.sortBy(row.junction.outputs, l => l.number).forEach(line => {
					rows.push(mapLineToRow(line));
				});
				return;
			}

			let junction = row.line.endJunction;
			if (!junction) {
				return;
			}

			// do not add the junction if it is already in the list of rows
			if (_.some(rows, r => !r.isLine && r.junction === junction)) {
				return;
			}

			// if the end junction references lines not in the previous area, add the line instead
			if (!junction.inputs.every(l => _.some(previousLines, al => al.id === l.id))) {
				rows.push(mapLineToRow(row.line));
			}

			rows.push(mapJunctionToRow(junction));
		});

		area = { rows: rows };
	}
	return areas;
}

function getCountsHelpText(helpScreen: string): string {
	let helptext = '';

	switch (helpScreen) {
		case 'EQUIPMENT COUNTS':
			helptext = `  
			<div class="header">
			  Equipment Counts
			</div>         
			<div class="subheader">
			  <u>How the Counts Work</u>:
			</div>
			<div class="detail">
				Below is a sample count object, this is sample data just for instructional purposes.
			</div>
			<div class="detail">
				<table>
					<tbody>
						<tr>
							<td>
								<div class="bs-card">
									<div class="equipment-name">SQR</div>
									<div class="count">11,328</div>
									<div>
										<span>166</span>
										<span class="fail-percentage">1.5%</span>
									</div>
									<br/>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="detail">
				As you can see the count object is broken up into 4 different rows. Here is the definition of each row:
			</div>  
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>Equipment Shorthand:</b> This is a shorthand abreviation of the type of equipment configured on the line.
						In this case this is a high speed squeezer. This is abreviated as SQR.
					</li>
					<li class="detail">
						<b>Inspect Count:</b> The number of bottles going into the machine.
						In APS the preference is to use a direct integration to the machine for this counter.
						However, there are instances that we are dependent on a photoeye before the inspection equipment 
						to provide the inspect count.
					</li>
					<li class="detail">
						<b>Reject Count:</b>
						The amount of bottles rejected by the piece of inspection equipment.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						For most equipment we have a direct integration and the equipment keeps counts of rejects.
						However, for some equipment integrations we need to calculated it by taking the difference between
						bottles in and bottles out.
						<br/>
						<span class="formula">Reject Count = Reject Count from Machine</span><br/>
						<span class="count">- OR -</span><br/>
						<span class="formula">Reject Count = Machine Bottles In - Machine Bottles Out</span><br/>
					 </li>
					<li class="detail">
						<b>Reject Percent:</b>
						The percent of loss for each piece of equipment.
						This calculation is based off the equipment inspects vs. the rejects.
						<br/>
						<span class="formula">Reject Percent = Reject Count / Inspect Count</span><br/>

						On each job run, an acceptable percentage range can be set for each equipment type. If the equipment has a tolerance set
						and the current value is outside those parameters, this value is highlighted in 
						<span class="red">red</span> to help users quickly identify issues.
					</li>
				</ol>
			</div>
			<div class="subheader">
				<u>Equipment Details</u>:
			</div>   
			<div class="detail">
				Every piece of equipment has the ability to drill-down. 
				All the user has to do to access this data is to click on the peice of equipment.
				Depending on the type of integration some equipment will display more data than others.
			</div>
		  	`;
			break;
		case 'AREA EFFICIENCY':
			helptext = `  
			<div class="header">
			  Area Efficiency
			</div>         
			<div class="subheader">
			  <u>Metrics per Area</u>:
			</div>
			<div class="detail">
				Each area of the production line will have the following metrics, where applicable.
				The metric definitions are as follows.
			</div>
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>AREA IN:</b> 
						The total number of bottles going into each area.
						This number is used as the denominator for efficiency calculations in each area.
					</li>
					<li class="detail">
						<b>AREA REJECTS:</b> 
						The total amount of bottles rejected in each area.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
					</li>
					<li class="detail">
						<b>AREA REJECT:</b>
						The percent of loss for each area.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">AREA REJECT = 100 * (Area Rejects / Area In)</span>
					</li>
					<li class="detail">
						<b>AREA EFFICIENCY:</b>
						The ammount of bottles to make it through each area compared to how many went in.
						This is the inverse of the <i>AREA REJECT</i>.
						<br/>
						<span class="formula">AREA REJECT = 100 - (100 * (Area Rejects / Area In))</span>
					</li>
				</ol>
			</div>
		  	`;
			break;
		case 'SHOP KPI':
			helptext = `  
			<div class="header">
			  Shop KPI's
			</div>         
			<div class="subheader">
			  <u>KPI Metric Definitions</u>:
			</div>
			<div class="detail">
				The goal of these KPI's is to see how the shop is performing at each point along the line.
				We use each pulse from the shear cuts to calculate how many gobs are currently being produced.
			</div>
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>MACHINE SPEED:</b>
						How many bottles per minute the machine is producing.
						When available we use direct machine integration.
						Otherwise, we use the shear pulses to estimate the speed.
					</li>
					<li class="detail">
						<b>LEHR IN %:</b> 
						The percent of bottles going into the lehr, against the shears.
						<br/>
						<span class="formula">LEHR IN % = Lehr In Counter / Shears</span>
					</li>
					<li class="detail">
						<b>LEHR OUT %:</b>
						The percent of bottles comming out of the lehr, into the cold-end, against the shears.
						Usually we are using the sum of the first peice of inspection equipment.
						<br/>
						<span class="formula">LEHR OUT % = Cold-End In / Shears</span>
					</li>
					<li class="detail">
						<b>PACK %:</b>
						The percent of bottles that make it to the End of Line (EOL), against the shears.
						This is usually a photoeye, sometimes it can be the last piece of inspection equipment.
						It depends on the shop configuration.
						<br/>
						<span class="formula">PACK % = EOL / Shears</span>
					</li>
				</ol>
			</div>
		  	`;
			break;
		case 'WARE LOSS':
			helptext = `  
			<div class="header">
				Shop Ware Loss
			</div>         
			<div class="subheader">
				<u>Shop Ware Loss Definitions</u>:
			</div>
			<div class="detail">
				The goal of these Metrics are quickly identify the areas of most loss compared to bottles created.
				We use each pulse from the shear cuts to calculate how many gobs are produced at the hot-end.
				Your total loss should add up to all the other ware loss percentages.
			</div>
			<div class="detail">
				<ol type = "1">
					<li class="detail">
						<b>HOT END LOSS:</b>
						The percent of the bottles produced that did not make it into the lehr.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">HOT END LOSS = 100 - (100 * (LEHR IN / SHEARS))</span>
					</li>
					<li class="detail">
						<b>LEHR LOSS:</b> 
						The percent of bottles that were lost in the lehr, over the shears.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">LEHR LOSS = 100 - (100 * ((LEHR IN - LEHR OUT) / SHEARS))</span>
					</li>
					<li class="detail">
						<b>AIE LOSS:</b>
						The percent of bottles lost to all inspection equipment, over the shears.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">AIE LOSS = 100 - (100 * (AIE LOSS / SHEARS))</span>
					</li>
					<li class="detail">
						<b>CE DELTA LOSS:</b>
						The percent of all bottles that made it into the cold end, 
						that were not rejected by inspection equipment,
						but did not make it to the end of line,
						over the shears.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">CE DELTA LOSS = 100 - (100 * (LEHR OUT - AIE LOSS - END OF LINE) / SHEARS)</span>
					</li>
					<li class="detail">
						<b>TOTAL LOSS:</b>
						The percent of bottles that did not make it to the End of Line (EOL), compared to the shears.
						This is usually a photoeye, sometimes it can be the last piece of inspection equipment.
						It depends on the shop configuration.
						This value is highlighted in <span class="red">red</span> to help users quickly identify losses.
						<br/>
						<span class="formula">TOTAL LOSS = 100 - (100 * (END OF LINE / SHEARS))</span>
					</li>
				</ol>
			</div>
		  	`;
			break;
		default:
			helptext = 'Selected Help Screen Not Found...';
			break;
	}

	return helptext;
}




// WEBPACK FOOTER //
// ./src/app/controllers/counts/count.controller.ts